import type { ServerPackage, CreatePackageRequest, UpdatePackageRequest, GameFieldConfig } from '../types/package';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class PackageService {
  private static getHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  static async getAllPackages(game?: string, includeInactive?: boolean): Promise<ServerPackage[]> {
    const params = new URLSearchParams();
    if (game) params.append('game', game);
    if (includeInactive) params.append('includeInactive', 'true');

    const response = await fetch(`${API_BASE_URL}/packages?${params}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch packages');
    }

    const data = await response.json();
    return data.packages;
  }

  static async getPackageById(id: string): Promise<ServerPackage> {
    const response = await fetch(`${API_BASE_URL}/packages/${id}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch package');
    }

    const data = await response.json();
    return data.package;
  }

  static async getPackagesByGame(gameType: string): Promise<ServerPackage[]> {
    const response = await fetch(`${API_BASE_URL}/packages/game/${gameType}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch packages for game');
    }

    const data = await response.json();
    return data.packages;
  }

  static async getGameFieldConfigs(gameType: string): Promise<GameFieldConfig[]> {
    const response = await fetch(`${API_BASE_URL}/packages/game/${gameType}/fields`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch field configs');
    }

    const data = await response.json();
    return data.fieldConfigs;
  }

  static async getPackageDefaultFieldConfigs(gameType: string): Promise<GameFieldConfig[]> {
    const response = await fetch(`${API_BASE_URL}/packages/game/${gameType}/package-fields`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch package default field configs');
    }

    const data = await response.json();
    return data.fieldConfigs;
  }

  static async createPackage(packageData: CreatePackageRequest): Promise<ServerPackage> {
    const response = await fetch(`${API_BASE_URL}/packages`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(packageData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create package');
    }

    const data = await response.json();
    return data.package;
  }

  static async updatePackage(id: string, packageData: UpdatePackageRequest): Promise<ServerPackage> {
    const response = await fetch(`${API_BASE_URL}/packages/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(packageData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update package');
    }

    const data = await response.json();
    return data.package;
  }

  static async deletePackage(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/packages/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete package');
    }
  }
}
